import { Fragment, useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../../hooks/useApi';
import { fmtDateTime } from '../../../../utils/ruDates';
import {
    estimateFaq, estimateFaqAnswers, generateFaqAnswers, getFaqRun, listFaqRuns, runFaq, sendFaqToKnowledge, updateFaqEntry,
} from '../../../../services/feedback';
import { errorText, money } from './feedbackUi';

const STATUS = { queued: 'В очереди', running: 'Идёт', done: 'Готово', failed: 'Не удалось' };
const LEVELS = [['sku', 'По артикулам'], ['model', 'По моделям']];
const SOURCES = { store_reply: 'из ответов магазина на Ozon', facts: 'из фактов о товаре', manual: 'вручную' };
const NOTES = { exists: 'такой вопрос уже есть в базе знаний', limit: 'в базе знаний уже 30 вопросов', no_target: 'нет карточки товара в базе знаний' };
const PRESETS = [[30, '30 дней'], [90, '90 дней'], [180, '180 дней'], [365, 'Год'], [3650, 'Всё время']];
const dateRu = (iso) => (iso ? iso.split('-').reverse().join('.') : '');
const range = (item) => `${money(item.price_min_rub)} – ${money(item.price_max_rub)}`;

// Одна тема: вопрос, сколько раз спрашивали, редактируемый ответ. Ответ сохраняется, когда поле теряет фокус.
function EntryRow({ entry, runId, checked, onCheck, onSaved }) {
    const { request } = useApi();
    const [answer, setAnswer] = useState(entry.answer);
    const [error, setError] = useState('');
    useEffect(() => { setAnswer(entry.answer); }, [entry.answer]);

    const save = async () => {
        if (answer === entry.answer) return;
        setError('');
        try {
            onSaved(await updateFaqEntry({ request, runId, entryId: entry.id, answer }));
        } catch (err) {
            setError(errorText(err));
        }
    };
    return (
        <tr>
            <td><input type="checkbox" aria-label="Выбрать тему" checked={checked} onChange={() => onCheck(entry.id)} /></td>
            <td style={{ maxWidth: 220 }}>
                <div>{entry.group_label || '—'}</div>
                <div className="fb-item__meta">{entry.skus.length > 1 ? `${entry.skus.length} артикулов` : `SKU ${entry.skus[0] || '—'}`}</div>
            </td>
            <td style={{ maxWidth: 260 }}>
                {entry.question}
                {entry.store_replies.length > 0 && (
                    <details>
                        <summary className="fb-note" style={{ cursor: 'pointer' }}>Что магазин уже отвечал</summary>
                        {entry.store_replies.map((reply, index) => <div key={index} className="fb-item__meta" style={{ marginTop: 4 }}>{reply}</div>)}
                    </details>
                )}
            </td>
            <td className="fb-num">{entry.count}</td>
            <td style={{ minWidth: 280 }}>
                <textarea className="fb-textarea" rows={3} maxLength={800} value={answer} placeholder="Ответ для базы знаний: напишите сами или сгенерируйте"
                    onChange={(event) => setAnswer(event.target.value)} onBlur={save} />
                <div className="fb-item__meta">{entry.answer_source ? SOURCES[entry.answer_source] : 'ответа пока нет'}</div>
                {error && <div className="fb-note fb-chip--bad">{error}</div>}
            </td>
            <td>
                {entry.sent
                    ? <span className="fb-chip fb-chip--good">в базе знаний</span>
                    : <span className="fb-note">{NOTES[entry.send_note] || (entry.answer ? 'готово к отправке' : '')}</span>}
            </td>
        </tr>
    );
}

function RunView({ runId, onBack }) {
    const { request } = useApi();
    const [run, setRun] = useState(null);
    const [picked, setPicked] = useState(() => new Set());
    const [answersPrice, setAnswersPrice] = useState(null);
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);

    const reload = useCallback(async () => {
        try {
            setRun(await getFaqRun({ request, runId }));
        } catch (err) {
            setMessage(errorText(err));
        }
    }, [request, runId]);

    useEffect(() => { reload(); }, [reload]);
    useEffect(() => {
        if (!run?.generating) return undefined;
        const timer = setTimeout(reload, 4000);
        return () => clearTimeout(timer);
    }, [run, reload]);

    const entries = run?.result?.entries || [];
    const ids = [...picked];
    const withoutAnswer = (list) => list.filter((entry) => !entry.answer);
    const target = picked.size ? entries.filter((entry) => picked.has(entry.id)) : entries;

    useEffect(() => {
        let cancelled = false;
        setAnswersPrice(null);
        if (!run?.result || withoutAnswer(target).length === 0) return undefined;
        estimateFaqAnswers({ request, runId, entryIds: ids }).then((data) => !cancelled && setAnswersPrice(data)).catch(() => {});
        return () => { cancelled = true; };
    }, [request, runId, picked, run?.result?.entries]);

    const replaceEntry = (updated) => setRun((previous) => ({
        ...previous,
        result: { ...previous.result, entries: previous.result.entries.map((entry) => (entry.id === updated.id ? updated : entry)) },
    }));
    const toggle = (id) => setPicked((previous) => {
        const next = new Set(previous);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const groups = [];
    entries.forEach((entry) => {
        let group = groups.find((item) => item.key === entry.group_key);
        if (!group) {
            group = { key: entry.group_key, label: entry.group_label || entry.group_key, items: [], skus: new Set(), total: 0 };
            groups.push(group);
        }
        group.items.push(entry);
        (entry.skus || []).forEach((sku) => group.skus.add(sku));
        group.total += entry.count || 0;
    });
    const toggleGroup = (items) => setPicked((previous) => {
        const next = new Set(previous);
        const all = items.every((entry) => next.has(entry.id));
        items.forEach((entry) => (all ? next.delete(entry.id) : next.add(entry.id)));
        return next;
    });
    const toggleAll = () => setPicked(picked.size === entries.length ? new Set() : new Set(entries.map((entry) => entry.id)));

    const generate = async () => {
        setBusy(true);
        setMessage('');
        try {
            await generateFaqAnswers({ request, runId, entryIds: ids });
            setMessage('Ответы генерируются в фоне: можно уйти со страницы. Набранные вручную ответы не меняются.');
            await reload();
        } catch (err) {
            let text = errorText(err);
            try {
                if (JSON.parse(err.message)?.code === 'answers_running') text = 'Ответы уже генерируются';
            } catch (parseError) {
                // обычное сообщение
            }
            setMessage(text);
        } finally {
            setBusy(false);
        }
    };
    const send = async () => {
        setBusy(true);
        setMessage('');
        try {
            const stats = await sendFaqToKnowledge({ request, runId, entryIds: ids });
            const skipped = stats.exists + stats.limit + stats.no_target;
            setMessage(`Отправлено в базу знаний: ${stats.sent}${skipped ? `, пропущено ${skipped} (причина в строке)` : ''}.`);
            await reload();
        } catch (err) {
            setMessage(errorText(err));
        } finally {
            setBusy(false);
        }
    };

    if (!run) return <div className="fb-card fb-muted">{message || 'Загрузка…'}</div>;
    const data = run.result;
    if (!data) return <div className="fb-card fb-muted">Сборка ещё не готова.</div>;
    const byModel = data.level === 'model';
    const ready = target.filter((entry) => entry.answer && !entry.sent).length;
    const note = data.answers_note;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="fb-row fb-spread">
                    <div>
                        <h4 style={{ margin: 0 }}>Вопросы и чаты {byModel ? 'по моделям' : 'по артикулам'}</h4>
                        <div className="fb-muted">
                            {dateRu(data.period.from)} – {dateRu(data.period.to)} · групп {data.groups} · тем {entries.length} · списано {money(run.price_rub)}
                        </div>
                    </div>
                    <button type="button" onClick={onBack}>К списку</button>
                </div>
                <div className="fb-row" style={{ alignItems: 'center' }}>
                    <button type="button" className="primary" disabled={busy || run.generating || withoutAnswer(target).length === 0} onClick={generate}
                        title="ИИ предложит ответы только из ответов магазина на Ozon и фактов о товаре. Услуга платная">
                        {run.generating ? 'Генерируются…' : `Сгенерировать ответы (${picked.size ? 'выбранные' : 'все без ответа'})`}
                    </button>
                    {answersPrice && withoutAnswer(target).length > 0 && (
                        <span className="fb-note">{answersPrice.themes} тем, стоимость от {money(answersPrice.price_min_rub)} до {money(answersPrice.price_max_rub)}</span>
                    )}
                    <button type="button" disabled={busy || ready === 0} onClick={send}
                        title={byModel ? 'Ответы попадут в базу знаний модели' : 'Ответы попадут в базу знаний артикула'}>
                        {`Отправить в базу знаний ${byModel ? 'модели' : 'артикула'} (${ready})`}
                    </button>
                </div>
                <div className="fb-note">
                    По умолчанию ответы пустые: заполните их сами или сгенерируйте. Ответ можно поправить, он сохраняется, когда вы выходите из поля.
                    Отправка в базу знаний идёт отдельной кнопкой; существующие вопросы базы знаний не меняются.
                </div>
                {note && !run.generating && (
                    <div className="fb-note">
                        {note.error ? `Генерация не удалась: ${note.error === 'insufficient_balance' ? 'не хватило денег на балансе' : note.error}.` : `Сгенерировано ответов: ${note.filled} из ${note.requested}.`}
                    </div>
                )}
                {data.errors > 0 && <div className="fb-note">Часть запросов к ИИ не удалась ({data.errors}), эти вопросы остались отдельными темами.</div>}
                {message && <div className="fb-note">{message}</div>}
            </div>

            <div className="fb-table-wrap">
                <table className="fb-table">
                    <thead>
                        <tr>
                            <th><input type="checkbox" aria-label="Выбрать все" checked={entries.length > 0 && picked.size === entries.length} onChange={toggleAll} /></th>
                            <th>{byModel ? 'Модель' : 'Товар'}</th><th>Вопрос</th><th className="fb-num">Раз</th><th>Ответ для базы знаний</th><th>База знаний</th>
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map((group) => (
                            <Fragment key={group.key}>
                                <tr className="fb-group-row">
                                    <td><input type="checkbox" aria-label="Выбрать все темы группы" checked={group.items.every((entry) => picked.has(entry.id))}
                                        onChange={() => toggleGroup(group.items)} /></td>
                                    <td colSpan={5}>
                                        <b>{group.label}</b>
                                        <span className="fb-item__meta"> · {byModel && !String(group.key).startsWith('sku:') ? `артикулов ${group.skus.size}, ` : ''}тем {group.items.length}, обращений {group.total}</span>
                                    </td>
                                </tr>
                                {group.items.map((entry) => (
                                    <EntryRow key={entry.id} entry={entry} runId={runId} checked={picked.has(entry.id)} onCheck={toggle} onSaved={replaceEntry} />
                                ))}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
                {entries.length === 0 && <div className="fb-empty">За период нет вопросов и чатов по товарам.</div>}
            </div>

            {data.gaps && (
                <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <h4 style={{ margin: 0 }}>Чего не хватило покупателям (по отзывам)</h4>
                    {data.gaps.length === 0 && <div className="fb-muted">В отзывах за период нехватки информации не найдено.</div>}
                    {data.gaps.map((group) => (
                        <div key={group.group_key} className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 6, background: 'transparent' }}>
                            <b>{group.group_label || group.group_key}</b>
                            <div className="fb-item__meta">отзывов с текстом разобрано {group.reviews}</div>
                            {group.summary && <div>{group.summary}</div>}
                            <ul className="fb-points">
                                {group.gaps.map((gap, index) => <li key={index}>{gap.text}{gap.count ? ` · ${gap.count}` : ''}</li>)}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function FeedbackFaq({ accountId }) {
    const { request } = useApi();
    const [preset, setPreset] = useState('365');
    const [level, setLevel] = useState('sku');
    const [includeGaps, setIncludeGaps] = useState(false);
    const [runs, setRuns] = useState([]);
    const [estimate, setEstimate] = useState(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [openId, setOpenId] = useState('');
    const options = { period: { days: Number(preset) }, level, includeGaps };

    const reload = useCallback(async () => {
        try {
            setRuns((await listFaqRuns({ request, accountId })).items);
        } catch (err) {
            // список запусков не критичен
        }
    }, [request, accountId]);

    useEffect(() => { reload(); }, [reload]);
    useEffect(() => { setEstimate(null); setMessage(''); }, [preset, level, includeGaps]);
    useEffect(() => {
        if (!runs.some((item) => item.status === 'queued' || item.status === 'running')) return undefined;
        const timer = setTimeout(reload, 5000);
        return () => clearTimeout(timer);
    }, [runs, reload]);

    const check = async () => {
        setBusy(true);
        setMessage('');
        try {
            setEstimate(await estimateFaq({ request, accountId, ...options }));
        } catch (err) {
            setMessage(errorText(err));
        } finally {
            setBusy(false);
        }
    };
    const start = async () => {
        setBusy(true);
        setMessage('');
        try {
            await runFaq({ request, accountId, ...options });
            setEstimate(null);
            setMessage('Сборка идёт в фоне: можно закрыть страницу. Когда она закончится, откройте её из списка ниже.');
            await reload();
        } catch (err) {
            let text = errorText(err);
            try {
                const code = JSON.parse(err.message)?.code;
                if (code === 'report_running') text = 'Предыдущая сборка ещё идёт';
                if (code === 'no_questions') text = 'За период нет вопросов и чатов по товарам';
            } catch (parseError) {
                // обычное сообщение
            }
            setMessage(text);
        } finally {
            setBusy(false);
        }
    };

    if (openId) return <RunView runId={openId} onBack={() => { setOpenId(''); reload(); }} />;

    return (
        <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
                <h4 style={{ margin: 0 }}>Вопросы и чаты</h4>
                <div className="fb-muted">
                    ИИ собирает вопросы к товарам и чаты покупателей за период и группирует похожие по смыслу внутри артикула или модели (вопрос, который встретился один раз, тоже тема).
                    Ответы не пишутся сами: их можно набрать вручную или сгенерировать отдельным запросом, поправить и отправить в базу знаний отдельной кнопкой.
                </div>
            </div>
            <div className="fb-row" style={{ alignItems: 'center' }}>
                <select className="fb-input" value={level} onChange={(event) => setLevel(event.target.value)} aria-label="Группировка">
                    {LEVELS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <select className="fb-input" value={preset} onChange={(event) => setPreset(event.target.value)} aria-label="Период">
                    {PRESETS.map(([value, label]) => <option key={value} value={String(value)}>{label}</option>)}
                </select>
                <button type="button" className="primary" disabled={busy} onClick={check}>Оценить стоимость</button>
                <label className="fb-note" title="За тот же период ИИ прочитает отзывы и найдёт, какой информации покупателям не хватило">
                    <input type="checkbox" checked={includeGaps} onChange={(event) => setIncludeGaps(event.target.checked)} /> найти недостаток информации в отзывах
                </label>
            </div>
            {estimate && (
                <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 6, background: 'transparent' }}>
                    {estimate.groups === 0 && !(includeGaps && estimate.reviews) ? (
                        <div>За период нет вопросов и чатов по товарам.</div>
                    ) : (
                        <>
                            <div>
                                В сборку войдёт <b>{estimate.groups}</b> {level === 'model' ? 'моделей' : 'товаров'}: {estimate.questions - estimate.chats} вопросов и {estimate.chats} сообщений из чатов
                                (группировать ИИ будет {estimate.grouped_by_ai}, остальные темы из одного вопроса).
                                {includeGaps ? ` Отзывов для поиска нехватки информации: ${estimate.reviews}.` : ''} Запросов к ИИ: {estimate.llm_calls}.
                            </div>
                            <div>Стоимость: <b>от {money(estimate.price_min_rub)} до {money(estimate.price_max_rub)}</b>, обычно около {money(estimate.price_rub)}. Списывается с баланса плательщика по факту.</div>
                            <div className="fb-note">{estimate.note}</div>
                            <div className="fb-row">
                                <button type="button" className="primary" disabled={busy} onClick={start}>Собрать</button>
                                <button type="button" onClick={() => setEstimate(null)}>Отмена</button>
                            </div>
                        </>
                    )}
                </div>
            )}
            {message && <div className="fb-note">{message}</div>}
            {runs.length > 0 && (
                <div className="fb-table-wrap">
                    <table className="fb-table">
                        <thead><tr><th>Создана</th><th>Что собрано</th><th>Состояние</th><th className="fb-num">Цена</th><th /></tr></thead>
                        <tbody>
                            {runs.map((item) => (
                                <tr key={item.id}>
                                    <td>{fmtDateTime(item.created_at)}</td>
                                    <td>
                                        {item.params.level === 'model' ? 'По моделям' : 'По артикулам'}, {item.params.date_from ? `${dateRu(item.params.date_from)} – ${dateRu(item.params.date_to)}` : `${item.params.days} дн.`}
                                        {item.params.include_gaps ? ' + нехватка информации' : ''}
                                    </td>
                                    <td>
                                        {STATUS[item.status] || item.status}
                                        {item.status === 'running' && item.total_steps > 0 ? ` ${item.progress} из ${item.total_steps}` : ''}
                                        {item.status === 'failed' && item.error ? `: ${item.error === 'insufficient_balance' ? 'не хватило денег на балансе' : item.error}` : ''}
                                    </td>
                                    <td className="fb-num">{item.price_rub && item.status === 'done' ? money(item.price_rub) : item.estimate ? range(item.estimate) : '—'}</td>
                                    <td>{item.status === 'done' && <button type="button" onClick={() => setOpenId(item.id)}>Открыть</button>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
